<template>
  <div class="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
        {{ $t('message.signinCaption') }}
      </h2>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <form class="space-y-6" @submit.prevent="handleSubmit">
          <div>
            <label for="username" class="block text-sm font-medium text-gray-700">
              {{ $t("message.usernameCaption") }}
            </label>
            <div class="mt-1">
              <input
                id="username"
                v-model="signInDetails.username"
                name="username"
                type="text"
                autocomplete="username"
                required
                :disabled="isLoading"
                :class="['appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm',
                  usernameError ? 'border-red-500' : 'border-gray-300']"
              />
            </div>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">
              {{ $t("message.passwordCaption") }}
            </label>
            <div class="mt-1">
              <input
                id="password"
                v-model="signInDetails.password"
                name="password"
                type="password"
                autocomplete="current-password"
                required
                :disabled="isLoading"
                :class="['appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm',
                  passwordError ? 'border-red-500' : 'border-gray-300']"
              />
            </div>
          </div>

          <!-- Google reCAPTCHA -->
          <div ref="recaptcha" class="flex justify-center" v-if="SHOW_CAPTCHA"></div>

          <div>
            <button
              type="submit"
              :disabled="!isFormValid || isLoading"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <template v-if="isLoading">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ $t("message.signingIn") }}
              </template>
              <template v-else>
                {{ $t("message.signinCaption") }}
              </template>
            </button>
          </div>

          <!-- VK Login Button -->
          <div class="mt-6">
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-300"></div>
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white text-gray-500"> {{ $t('message.or') }} </span>
              </div>
            </div>
          </div>
          <div ref="vkIdContainerRef" id="vk-id-onetap-container" class="mt-3 flex justify-center"></div>
        </form>

        <!-- Error Messages -->
        <div v-if="errors.length > 0" class="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong class="font-bold">{{ $t('message.errorTitle') }}</strong>
          <ul class="mt-1">
            <li v-for="(error, index) in errors" :key="index" class="block sm:inline">{{ error }}</li>
          </ul>
        </div>

        <div class="mt-6">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300" />
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white text-gray-500">
                {{ $t("message.notRegistredCaption") }}
              </span>
            </div>
          </div>
          <div class="mt-6 text-center">
            <router-link
              :to="{name: 'register'}"
              class="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {{ $t("message.registerCaption") }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '@/axios/axios';
import { useAuthStore } from '@/stores/user';
import { ref, nextTick } from 'vue';
import * as VKID from '@vkid/sdk';
import { useRouter, useRoute } from 'vue-router';

const VITE_RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const VITE_VK_CLIENT_ID = import.meta.env.VITE_VK_CLIENT_ID;

let isVkSdkConfigInitialized = false;
let vkOneTapInstance = null;

export default {
  setup() {
    const recaptcha = ref(null);
    const recaptchaToken = ref('');
    const isLoading = ref(false);
    const authStore = useAuthStore();
    const router = useRouter();
    const route = useRoute();

    const vkIdContainerRef = ref(null);

    const signInDetails = ref({
      username: "",
      password: "",
    });
    const errors = ref([]);
    const usernameError = ref(false);
    const passwordError = ref(false);

    const SHOW_CAPTCHA_CONST = import.meta.env.VITE_SHOW_CAPTCHA === 'true';

    const isFormValid = () => {
      return signInDetails.value.username &&
        signInDetails.value.password &&
        (!SHOW_CAPTCHA_CONST || recaptchaToken.value);
    };

    return {
      recaptcha,
      recaptchaToken,
      isLoading,
      authStore,
      router,
      route,
      signInDetails,
      errors,
      usernameError,
      passwordError,
      isFormValid,
      vkIdContainerRef,
      SHOW_CAPTCHA: SHOW_CAPTCHA_CONST,
    };
  },

  methods: {
    loadRecaptcha() {
      if (!this.SHOW_CAPTCHA) {
        return;
      }
      if (window.grecaptcha && window.grecaptcha.render) {
        this.renderRecaptcha();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=explicit&onload=onRecaptchaApiLoad`;
      script.async = true;
      script.defer = true;

      window.onRecaptchaApiLoad = () => {
        console.log('reCAPTCHA API script loaded via global onload.');
        this.renderRecaptcha();
      };
      script.onload = () => {
        if (!window.grecaptcha?.render) {
            console.log('reCAPTCHA API script loaded via script.onload, attempting render.');
            this.renderRecaptcha();
        }
      };
      script.onerror = () => {
        console.error('Failed to load reCAPTCHA API script.');
        this.errors.push(this.$t('message.recaptchaLoadError', 'Failed to load reCAPTCHA service.'));
      };

      document.head.appendChild(script);
    },

    renderRecaptcha() {
      if (!this.SHOW_CAPTCHA) {
        return;
      }
      if (!this.recaptcha || !this.recaptcha.value) {
          console.error('reCAPTCHA container ref (this.recaptcha.value) is not available for rendering.');
          return;
      }
      try {
        window.grecaptcha.ready(() => {
          console.log('grecaptcha is ready, rendering reCAPTCHA.');
          window.grecaptcha.render(this.recaptcha.value, {
            'sitekey': VITE_RECAPTCHA_SITE_KEY,
            'callback': this.handleRecaptchaToken,
            'expired-callback': () => {
              this.recaptchaToken = '';
            },
            'error-callback': () => {
              this.errors.push(this.$t('message.recaptchaError', 'reCAPTCHA rendering error.'));
            }
          });
        });
      } catch (error) {
        console.error('Error rendering reCAPTCHA:', error);
        this.errors.push(this.$t('message.recaptchaRenderError', 'Could not render reCAPTCHA.'));
      }
    },

    handleRecaptchaToken(token) {
      this.recaptchaToken = token;
    },

    resetErrors() {
      this.errors = [];
      this.usernameError = false;
      this.passwordError = false;
    },

    async verifyRecaptcha() {
      if (!this.SHOW_CAPTCHA) {
        return true;
      }
      if (!this.recaptchaToken) {
        this.errors.push(this.$t("message.recaptchaNotCompleted", "Please complete the reCAPTCHA."));
        return false;
      }
      try {
        const response = await axiosInstance.post("/api/auth/verify-recaptcha/", {
          response: this.recaptchaToken
        });
        return response.data.success;
      } catch (error) {
        console.error("reCAPTCHA verification failed:", error);
        this.errors.push(this.$t("message.recaptchaFailed", "reCAPTCHA verification failed."));
        return false;
      }
    },

    async initializeVkOneTap() {
      console.log('initializeVkOneTap called.');
      let containerElement = this.vkIdContainerRef?.value;

      if (!containerElement) {
        console.log('initializeVkOneTap: vkIdContainerRef.value is initially null/undefined. Checking direct DOM access as fallback.');
        containerElement = document.getElementById('vk-id-onetap-container');

        if (!containerElement) {
            console.log('initializeVkOneTap: Direct DOM access for #vk-id-onetap-container also failed. Attempting one more $nextTick and re-check.');
            await this.$nextTick();
            // Try ref first after tick, then direct DOM access again
            containerElement = this.vkIdContainerRef?.value || document.getElementById('vk-id-onetap-container');

            if (!containerElement) {
              console.error('VK OneTap container is STILL not available after direct DOM check and an additional $nextTick. VK button will not render.');
              if (this.$el) {
                const expectedParentInForm = this.$el.querySelector('form #vk-id-onetap-container');
                console.log('Final direct check for #vk-id-onetap-container inside form:', expectedParentInForm);
                const formElement = this.$el.querySelector('form');
                if (formElement) {
                    console.log('Final content of the form element:', formElement.innerHTML);
                } else {
                    console.error('Form element not found in component $el for detailed DOM inspection.');
                }
              } else {
                console.error('Component root element (this.$el) is not available for detailed DOM inspection.');
              }
              this.errors.push(this.$t('message.vkContainerError', 'Could not initialize VK Login button. UI element missing.'));
              return;
            }
        }
      }

      console.log('VK OneTap container element obtained:', containerElement);

      if (typeof VKID === 'undefined' || typeof window.VKIDSDK === 'undefined') {
        console.error('VK SDK (VKID or window.VKIDSDK) not loaded/available.');
        this.errors.push(this.$t('message.vkSdkLoadError', 'VK login service is unavailable. Please refresh.'));
        return;
      }

      if (!VITE_VK_CLIENT_ID || VITE_VK_CLIENT_ID.includes('YOUR_') || VITE_VK_CLIENT_ID === 'YOUR_VK_APP_ID_PLACEHOLDER') {
        console.error('VK Client ID (VITE_VK_CLIENT_ID) is not configured correctly.');
        this.errors.push(this.$t('message.vkConfigError', 'VK login is misconfigured. Contact support.'));
        return;
      }

      try {
        if (!isVkSdkConfigInitialized) {
          VKID.Config.init({
            app: parseInt(VITE_VK_CLIENT_ID, 10),
            redirectUrl: `${window.location.origin}/oauth/vk/callback`,
            state: 'your_csrf_protection_state_string',
            source: VKID.ConfigSource.LOWCODE,
          });
          isVkSdkConfigInitialized = true;
          console.log('VKID.Config.init successful.');
        }

        if (vkOneTapInstance && typeof vkOneTapInstance.destroy === 'function') {
          try {
            vkOneTapInstance.destroy();
            console.log('Previous VK OneTap instance destroyed.');
          } catch (e) {
            console.warn('Error destroying previous VK OneTap instance:', e);
          }
        }

        vkOneTapInstance = new VKID.OneTap();
        console.log('New VKID.OneTap instance created.');

        vkOneTapInstance
          .render({
            container: containerElement, // Use the resolved containerElement
            scheme: VKID.Scheme.LIGHT,
            showAlternativeLogin: true,
          })
          .on(VKID.WidgetEvents.ERROR, this.handleVkSdkGlobalError)
          .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, (payload) => {
            console.log('VK OneTap LOGIN_SUCCESS event:', payload);
            this.isLoading = true;
            this.errors = [];
            const { code, device_id } = payload;
            VKID.Auth.exchangeCode(code, device_id)
              .then(this.handleVkLoginSuccess)
              .catch(this.handleVkSdkGlobalError);
          });
        console.log('VK OneTap render called and event listeners attached.');

      } catch (sdkError) {
        console.error("Error during VK SDK setup or render:", sdkError);
        this.errors.push(this.$t('message.vkSdkSetupError', 'Failed to setup VK login widget.'));
        this.isLoading = false;
      }
    },

    async handleVkLoginSuccess(vkAuthData) {
      console.log('handleVkLoginSuccess invoked with data:', vkAuthData);
      this.isLoading = true;

      const tokenForBackend = vkAuthData.access_token;
      const vkUser = {
        id: vkAuthData.user_id || vkAuthData.user?.id,
        email: vkAuthData.email || vkAuthData.user?.email,
        first_name: vkAuthData.user?.first_name,
        last_name: vkAuthData.user?.last_name,
      };

      if (!tokenForBackend) {
        console.error('VK login successful, but access_token missing from vkAuthData.');
        this.errors.push(this.$t('message.vkTokenError', 'VK authentication successful, but token missing.'));
        this.isLoading = false;
        return;
      }

      try {
        const response = await axiosInstance.post('/api/auth/vk-login/', {
          access_token: tokenForBackend,
          email: vkUser.email,
          vk_user_id: vkUser.id,
          first_name: vkUser.first_name,
          last_name: vkUser.last_name,
        });

        console.log('Backend response from /api/auth/vk-login/:', response.data);
        if (response.data && response.data.access_token && response.data.user) {
          this.authStore.setTokens(response.data.access_token, response.data.refresh_token);
          this.authStore.setUser(response.data.user);
          const redirectPath = this.route.query.redirect || '/app';
          this.router.push(redirectPath);
        } else {
          console.error('Backend VK login did not return expected data (access_token or user).');
          this.errors.push(this.$t('message.vkLoginBackendError', 'Login after VK auth failed on our server.'));
        }
      } catch (error) {
        console.error('Error during backend VK login processing:', error.response ? error.response.data : error.message);
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          this.$t('message.vkLoginError', 'An error occurred with VK login.');
        if (!this.errors.includes(errorMessage)) this.errors.push(errorMessage);
      } finally {
        this.isLoading = false;
      }
    },

    handleVkSdkGlobalError(error) {
      console.error('VK SDK Global Error caught:', error);
      let errorMessage = this.$t('message.vkGenericError', 'VK login failed.');
      if (error) {
        if (typeof error === 'string') errorMessage = error;
        else if (error.description) errorMessage = `VK: ${error.description}`;
        else if (error.error_description) errorMessage = `VK: ${error.error_description}`;
        else if (error.payload?.error_description) errorMessage = `VK Event Error: ${error.payload.error_description}`;
        else if (error.payload?.description) errorMessage = `VK Event Error: ${error.payload.description}`;
        else if (error.message) errorMessage = `VK Error: ${error.message}`;
        else if (error.type) errorMessage = `VK Error Type: ${error.type}`;
      }
      if (!this.errors.includes(errorMessage)) this.errors.push(errorMessage);
      this.isLoading = false;
    },

    async handleSubmit() {
      this.resetErrors();
      if (!this.isFormValid()) {
         if (!this.signInDetails.username) this.errors.push(this.$t('message.usernameRequired', 'Username is required.'));
         if (!this.signInDetails.password) this.errors.push(this.$t('message.passwordRequired', 'Password is required.'));
         if (this.SHOW_CAPTCHA && !this.recaptchaToken) this.errors.push(this.$t('message.recaptchaNotCompleted', 'Please complete the reCAPTCHA.'));
        return;
      }

      this.isLoading = true;

      try {
        if (this.SHOW_CAPTCHA) {
          const isRecaptchaValid = await this.verifyRecaptcha();
          if (!isRecaptchaValid) {
            window.grecaptcha?.reset();
            this.isLoading = false;
            return;
          }
        }

        const success = await this.authStore.login({
          username: this.signInDetails.username,
          password: this.signInDetails.password,
        });

        if (success) {
          const redirect = this.route.query.redirect || '/app';
          this.router.push(redirect);
        } else {
          this.errors.push(this.authStore.getError || this.$t("message.loginFailed", "Login failed: Invalid credentials or server error."));
          this.usernameError = true;
          this.passwordError = true;
        }
      } catch (error) {
        console.error("Login handleSubmit error:", error);
        this.errors.push(this.$t("message.unexpectedError", "An unexpected error occurred during login."));
      } finally {
        this.isLoading = false;
      }
    }
  },

  async mounted() {
    console.log('SignIn.vue: mounted hook triggered.');
    console.log('this.SHOW_CAPTCHA value in mounted:', this.SHOW_CAPTCHA);

    if (this.SHOW_CAPTCHA) {
      this.loadRecaptcha();
    }

    console.log('SignIn.vue: About to wait for $nextTick in mounted.');
    await this.$nextTick();
    console.log('SignIn.vue: $nextTick in mounted has completed.');

    const directDomCheck = document.getElementById('vk-id-onetap-container');
    console.log('Direct DOM check for #vk-id-onetap-container in mounted (after $nextTick):', directDomCheck);
    console.log('Vue ref vkIdContainerRef object in mounted (after $nextTick):', this.vkIdContainerRef); // Log the ref object
    console.log('Vue ref vkIdContainerRef.value in mounted (after $nextTick):', this.vkIdContainerRef?.value); // Log its .value

    this.initializeVkOneTap();
  },

  beforeUnmount() {
    console.log('SignIn.vue: beforeUnmount hook triggered.');
    if (vkOneTapInstance && typeof vkOneTapInstance.destroy === 'function') {
      try {
        vkOneTapInstance.destroy();
        console.log('VK OneTap instance destroyed in beforeUnmount.');
      } catch (e) {
        console.error('Error destroying VK OneTap instance in beforeUnmount:', e);
      }
    }
    vkOneTapInstance = null;

    if (typeof window.onRecaptchaApiLoad === 'function' && window.onRecaptchaApiLoad.toString().includes('this.renderRecaptcha')) {
        delete window.onRecaptchaApiLoad;
    }
  }
};
</script>
